import { useEffect, useState } from 'react'
import { ApiClientError } from '../../api/client'
import { getDashboardTokenSavings, type TokenSavingsReport } from '../../api/token-savings'
import TokenSavingsSummary from './TokenSavingsSummary'

/**
 * Main-dashboard aggregate (Task 7): org admins/superadmins see org-wide
 * totals; project managers see the aggregate of projects they manage.
 * Regular viewers have no access in v1 — the panel renders nothing rather
 * than surfacing a forbidden error.
 *
 * `isOrgAdmin` only changes the subtitle copy — the backend (GET
 * /dashboard/token-savings) already decides the actual scope server-side
 * (org-wide for admins/superadmins, managed-only otherwise); this just makes
 * that distinction visible instead of always saying "projects you manage"
 * even when the numbers are already the whole organization's.
 */
export default function DashboardTokenSavingsCards({
  isOrgAdmin = false,
}: {
  isOrgAdmin?: boolean
}) {
  const [report, setReport] = useState<TokenSavingsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getDashboardTokenSavings({ hours: 24 })
      .then((r) => {
        if (!cancelled) setReport(r)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiClientError && err.status === 403) {
          setForbidden(true)
        } else {
          setError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (forbidden) return null
  if (loading) {
    return (
      <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
        {error}
      </div>
    )
  }
  if (!report) return null

  return (
    <div className="bg-harbor-surface border border-harbor-border rounded-xl">
      <div className="p-6 border-b border-harbor-border">
        <h2 className="text-lg font-semibold text-harbor-text">Token savings</h2>
        <p className="text-sm text-harbor-text-secondary mt-1">
          {isOrgAdmin
            ? 'Total across your organization — last 24h'
            : 'Across projects you manage — last 24h'}
        </p>
      </div>
      <div className="p-6">
        <TokenSavingsSummary report={report} />
      </div>
    </div>
  )
}
